import { TableContainer, Table, TableBody, TableHead, TableRow, TableCell } from '@mui/material'
import { toast } from 'react-toastify'

import ChargeRow from 'components/bookings/BookingDetails/OtherCharges/ChargeTable/ChargeRow'
import {
  isActiveOutOfHoursCharge,
  representsOutOfHours,
} from 'constants/EntityConstants/OtherChargeEntity'
import { usePermission } from 'providers/PermissionProvider'

interface IProps {
  fields: Record<any, any>[]
  update: any
  remove: any
  disabled: boolean
  isOutOfHours: boolean
  onInfoClick: (index: number) => void
}

const ChargeTable = (props: IProps) => {
  const { fields, update, remove, disabled, isOutOfHours, onInfoClick } = props

  const [isAllowedToViewVatCategories] = usePermission('ViewVatCategoryList')
  const [isAllowedToDeleteCharge] = usePermission('DeleteBookingCharge')
  const [isAllowedToEditCharge] = usePermission('EditBookingCharge')

  const showActionsColumn =
    isAllowedToViewVatCategories || isAllowedToDeleteCharge || isAllowedToEditCharge

  const visibleFields = fields
    ?.map((item, index) => ({ item, index }))
    .filter(({ item }) => {
      if (!item.isAutomatic) {
        return !item.isDeleted
      }

      if (!representsOutOfHours(item)) {
        return true
      }

      return !item.isDeleted || isOutOfHours
    })

  const onRemoveClick = async (index: number, item: Record<any, any>) => {
    if (item.isAutomatic) {
      update(index, {
        ...item,
        isDeleted: true,
      })
      toast.info(`Automatic charge ${item.name} marked as inactive`)
      return
    }

    remove(index)
    toast.info(`Charge ${item.name} was removed`)
  }

  const onActivateClick = async (index: number, item: Record<any, any>) => {
    if (!item.isAutomatic) {
      return
    }

    const hasOtherActiveOutOfHoursCharge = fields.some(
      (field, fieldIndex) => fieldIndex !== index && isActiveOutOfHoursCharge(field),
    )

    if (representsOutOfHours(item) && hasOtherActiveOutOfHoursCharge) {
      toast.error('Out Of Hours charge is already applied to this booking.')
      return
    }

    update(index, {
      ...item,
      isDeleted: false,
    })
    toast.info(`Automatic charge ${item.name} marked as active`)
  }

  return (
    <TableContainer sx={{ height: '300px' }}>
      <Table stickyHeader size='small' aria-label='charge table'>
        <TableHead>
          <TableRow>
            <TableCell>Charge</TableCell>
            <TableCell>Qty</TableCell>
            <TableCell>Automatic</TableCell>
            <TableCell>Status</TableCell>
            <TableCell>Rate (£)</TableCell>
            {showActionsColumn && <TableCell align='right'>Actions</TableCell>}
          </TableRow>
        </TableHead>
        <TableBody>
          {visibleFields?.map(({ item, index }) => (
            <ChargeRow
              row={item}
              index={index}
              key={item.chargeKey}
              disabled={disabled || item.isDeleted}
              isAllowedToViewVatCategories={isAllowedToViewVatCategories}
              isAllowedToDeleteCharge={isAllowedToDeleteCharge}
              isAllowedToEditCharge={isAllowedToEditCharge}
              showActionsColumn={showActionsColumn}
              onRemoveClick={onRemoveClick}
              onActivateClick={onActivateClick}
              onInfoClick={onInfoClick}
            />
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  )
}

export default ChargeTable
